import {
  all,
  any,
  equals,
  find,
  forEach,
  head,
  isEmpty,
  isNil,
  map,
  omit,
  pick,
  pipe,
  prop,
  propEq,
  takeLast,
} from "ramda";
import moment from "moment";

import { postOrder, registerCard } from "interfaces/order";
import { putConsumerInfo, postPhoneNumber } from "interfaces/consumer_info";
import getOrderingInfo, {
  getItemStockLevelInfo,
} from "interfaces/ordering_info";

import { getPrettyUrl } from "get_base_uri";

import isLoggedIn from "is_logged_in";
import Validators, { phoneRegex } from "helpers/validation/validators";
import VALIDATION_MESSAGES from "helpers/validation/messages";
import {
  validateBlurredField,
  validateCardDetails,
} from "helpers/validation/forms";
import { findCardTypeByFirstDigit } from "helpers/credit_cards";
import orderTransform from "helpers/transforms/online_ordering/order_transform";
import { GENERIC_ERROR } from "helpers/online_ordering/checkout";

import {
  orderSuccess,
  parseOrderSubmissionError,
  returnToMenu,
  storeTypeAndLastFour,
  updateContactInfo,
  updateOrderStatus,
  updateOrderStatusWithError,
} from "actions/online_ordering/checkout";
import { emptyCart } from "actions/online_ordering/cart";
import { setOrderTimeError } from "actions/online_ordering/ordering_options";

export const UPDATE_ORDERING_INFO = "online_ordering/UPDATE_ORDERING_INFO";
export const SUBMIT_ORDER = "online_ordering/SUBMIT_ORDER";
export const RETURN_CHECKOUT_FORM_ERRORS =
  "online_ordering/RETURN_CHECKOUT_FORM_ERRORS";
export const CLEAR_CHECKOUT_FORM_ERRORS =
  "online_ordering/CLEAR_CHECKOUT_FORM_ERRORS";
export const VALIDATE_CC_FIELD_ON_BLUR =
  "online_ordering/VALIDATE_CC_FIELD_ON_BLUR";
export const VALIDATE_CONTACT_FIELD_ON_BLUR =
  "online_ordering/VALIDATE_CONTACT_FIELD_ON_BLUR";

const contactInfoValidators = {
  firstName: Validators.isNotEmpty,
  lastName: Validators.isNotEmpty,
  phone: Validators.isValidPhoneNumber,
  email: Validators.isValidEmail,
};

const shortCardFormValidators = {
  cardNumber: Validators.isValidCardNumber,
  cvv: Validators.isValidCvv,
  expiryMonth: Validators.isValidMonth,
  expiryYear: Validators.isValidExpirationYear,
  firstName: Validators.isNotEmpty,
  lastName: Validators.isNotEmpty,
};

function validateContactInfo(contactInfo, validators) {
  const errors = [];

  if (!Validators.isValid(contactInfo, validators, errors)) {
    return pick(errors, VALIDATION_MESSAGES);
  }
  // HACK: this section needs rewriting it's duplicating a lot of different checks
  // but we have to solve an issue for a customer immediately
  let sanitizedErrors = contactInfo.errors;
  if (!isEmpty(contactInfo.errors)) {
    // We know that the following fields are valid even if they haven't been omitted
    // from state yet
    sanitizedErrors = omit(["firstName", "lastName", "email"], sanitizedErrors);
  }

  if (!isEmpty(sanitizedErrors)) {
    // These could contain errors from phone NANP validation

    return sanitizedErrors;
  }

  return null;
}

function validateContactFieldOnBlur(field, contact) {
  const error = validateBlurredField(
    field,
    contact,
    contactInfoValidators,
    VALIDATION_MESSAGES
  );

  return {
    type: VALIDATE_CONTACT_FIELD_ON_BLUR,
    error,
    field,
  };
}

function clearCheckoutFormErrors() {
  return {
    type: CLEAR_CHECKOUT_FORM_ERRORS,
  };
}

function returnContactErrors(error, field) {
  return {
    type: VALIDATE_CONTACT_FIELD_ON_BLUR,
    error,
    field,
  };
}

// Returns an array of errors when validating the fields
function validateAllContactInfo(contact) {
  const contactFields = ["firstName", "lastName", "email", "phone"];
  // Return an error object with a null or string error.
  return map(field => {
    const error = validateBlurredField(
      field,
      contact,
      contactInfoValidators,
      VALIDATION_MESSAGES
    );
    return { field, error };
  }, contactFields);
}

function validateAndPutContactInfo(field, contact, saveOnBlur = false) {
  const fieldValidationErrors = validateAllContactInfo(contact);
  let nanpValidation;
  let formattedPhone;
  return dispatch => {
    // Check there are no errors
    if (all(pipe(prop("error"), isNil), fieldValidationErrors)) {
      nanpValidation = Validators.validatePhoneNANP(contact);
      if (nanpValidation.allowSave) {
        formattedPhone = contact.phone.replace(
          phoneRegex,
          nanpValidation.regex
        );
        dispatch(updateContactInfo({ phone: formattedPhone }));
      }

      if (isLoggedIn() && saveOnBlur) {
        if (equals(field, "phone")) {
          // These functions should not be returned we need to save the data AND
          // clear the error if there is one which happens in the action below
          if (nanpValidation.allowSave) postPhoneNumber(formattedPhone);
        } else {
          putConsumerInfo(
            contact.firstName,
            contact.lastName,
            contact.textAlerts
          );
        }
      }
    }
    forEach(error => {
      dispatch(returnContactErrors(error.error, error.field));
    }, fieldValidationErrors);
    if (
      !isNil(nanpValidation) &&
      !isNil(nanpValidation.error) &&
      contact.textAlerts // Don't show the NANP error if text updates is unchecked
    )
      dispatch(returnContactErrors(nanpValidation.error, nanpValidation.field));
  };
}

function validateAndSaveContactField(field, contact) {
  const fieldValidationErrors = validateAllContactInfo(contact);
  let nanpValidation;
  let formattedPhone;
  return dispatch => {
    if (
      (equals(field, "phone") || equals(field, "textAlerts")) &&
      isNil(find(propEq("field", "phone"), fieldValidationErrors).error)
    ) {
      nanpValidation = Validators.validatePhoneNANP(contact);
      if (nanpValidation.allowSave) {
        formattedPhone = contact.phone.replace(
          phoneRegex,
          nanpValidation.regex
        );
        dispatch(updateContactInfo({ phone: formattedPhone }));
      }
    }
    forEach(error => {
      if (
        equals(error.field, field) ||
        ((equals(field, "phone") || equals(field, "textAlerts")) &&
          equals(error.field, "phone"))
      ) {
        dispatch(returnContactErrors(error.error, error.field));
      }
    }, fieldValidationErrors);
    if (
      !isNil(nanpValidation) &&
      !isNil(nanpValidation.error) &&
      contact.textAlerts // Don't show the NANP error if text updates is unchecked
    )
      dispatch(returnContactErrors(nanpValidation.error, nanpValidation.field));
  };
}

function updateOrderingInfo(orderingInfo) {
  return {
    type: UPDATE_ORDERING_INFO,
    orderingInfo,
  };
}

function fetchMenu() {
  return dispatch => {
    Promise.all([getOrderingInfo(), getItemStockLevelInfo()])
      .then(([orderingInfo, stockLevels]) => {
        dispatch(updateOrderingInfo({ ...orderingInfo, stockLevels }));
      })
      .catch(e => console.error(e));
  };
}

function returnCheckoutFormErrors(contactFormErrors, ccFormErrors) {
  return {
    type: RETURN_CHECKOUT_FORM_ERRORS,
    contactFormErrors,
    ccFormErrors,
  };
}

function orderTimeExpired(orderingOptions, storeInfo) {
  if (equals(orderingOptions.selectedTimeFrame, "Later")) {
    const methodDetails = find(
      propEq("name", orderingOptions.selectedTransport),
      storeInfo.fulfillmentTypes
    );
    const estimateWindow = methodDetails ? methodDetails.estimate[1] : 0;

    const orderTime = moment(
      orderingOptions.orderAheadTime.replace(/, \w{3}/g, ""),
      "h:mm A"
    );
    const orderDayAndTime = orderingOptions.orderAheadDate.dayAsMoment
      .clone()
      .set({
        hour: orderTime.hour(),
        minute: orderTime.minute(),
      });
    return orderDayAndTime.isBefore(moment().add(estimateWindow, "minutes"));
  }

  return false;
}

function submitOrder(
  storeInfo,
  cart,
  checkout,
  consumerInfo,
  orderingOptions,
  fee,
  deliveryTaxRate,
  featureFlags,
  sessionId,
  returnOnSuccess = true,
  cardValidationParams = {}
) {
  if (orderTimeExpired(orderingOptions, storeInfo)) {
    return dispatch => {
      dispatch(setOrderTimeError());
    };
  }

  const contactFormErrors = validateContactInfo(
    consumerInfo.contactInfo,
    contactInfoValidators
  );

  if (contactFormErrors) {
    return dispatch => {
      dispatch(updateOrderStatus("pending"));
      dispatch(returnCheckoutFormErrors(contactFormErrors, null));
    };
  }

  const orderInfo = orderTransform(
    storeInfo,
    checkout,
    consumerInfo,
    cart,
    orderingOptions,
    cardValidationParams,
    sessionId
  );

  return dispatch => {
    dispatch(clearCheckoutFormErrors());
    // TODO: Duplicated in checkout.jsx, should look into removing this.
    dispatch(updateOrderStatus("submitting"));

    postOrder(orderInfo)
      .then(() => {
        if (
          any(
            payment => equals("CREDIT", payment.paymentType),
            orderInfo.order.payments.payments
          ) &&
          isEmpty(cardValidationParams)
        ) {
          const card = find(
            propEq("dropdownFormat", checkout.payment.selectedCard),
            consumerInfo.cards
          );

          dispatch(storeTypeAndLastFour(card.lastFour, card.cardType));
        }

        let promisedTime = {
          orderTime: moment(orderInfo.order.timePlaced),
          isOrderAhead: false,
        };

        if (equals(orderingOptions.selectedTimeFrame, "Later")) {
          promisedTime = {
            orderTime: moment(orderInfo.order.promisedDate),
            isOrderAhead: true,
          };
        }

        dispatch(
          orderSuccess({
            orderStatus: "success",
            confirmationCode: orderInfo.order.confirmationCode,
            ...promisedTime,
          })
        );
        dispatch(emptyCart());
        if (returnOnSuccess) {
          dispatch(returnToMenu());
        }
      })
      .catch(({ response }) => {
        dispatch(parseOrderSubmissionError(response));
      });
  };
}

function registerCardAndSubmitOrder(
  storeInfo,
  cart,
  checkout,
  consumerInfo,
  orderingOptions,
  fee,
  deliveryTaxRate,
  featureFlags,
  paymentMethodToken,
  sessionId,
  returnOnSuccess
) {
  if (orderTimeExpired(orderingOptions, storeInfo)) {
    return dispatch => {
      dispatch(setOrderTimeError());
    };
  }

  const contactFormErrors = validateContactInfo(
    consumerInfo.contactInfo,
    contactInfoValidators
  );

  const ccFormErrors = featureFlags.uoloShorterPaymentForm
    ? validateCardDetails(
        checkout.payment.newCreditCard,
        shortCardFormValidators
      )
    : validateCardDetails(checkout.payment.newCreditCard);

  if (contactFormErrors || ccFormErrors) {
    return dispatch => {
      dispatch(updateOrderStatus("pending"));
      dispatch(returnCheckoutFormErrors(contactFormErrors, ccFormErrors));
    };
  }

  const prettyUrl = getPrettyUrl();
  const {
    firstName,
    lastName,
    cardNumber,
    cvv,
  } = checkout.payment.newCreditCard;
  const cardInfo = {
    firstName,
    lastName,
    paymentMethodToken,
    lastFour: takeLast(4, cardNumber),
    nameOnCard: `${firstName} ${lastName}`,
    cardType: findCardTypeByFirstDigit(
      head(cardNumber)
    ).prettyName.toLowerCase(),
    storePrettyUrl: prettyUrl,
  };

  return dispatch => {
    dispatch(clearCheckoutFormErrors());
    dispatch(storeTypeAndLastFour(cardInfo.lastFour, cardInfo.cardType));

    registerCard(cardInfo)
      .then(({ consumerId, cardId }) => {
        dispatch(
          submitOrder(
            storeInfo,
            cart,
            checkout,
            consumerInfo,
            orderingOptions,
            fee,
            deliveryTaxRate,
            featureFlags,
            sessionId,
            returnOnSuccess,
            {
              consumerUuid: consumerId,
              cardUuid: cardId,
              cvv,
            }
          )
        );
      })
      .catch(({ response }) => {
        if (
          response &&
          response.status &&
          response.status >= 400 &&
          response.status < 500
        ) {
          dispatch(
            updateOrderStatusWithError("submitOrder", GENERIC_ERROR, false)
          );
        } else {
          dispatch(
            updateOrderStatusWithError(
              "submitOrder",
              "Sorry, online ordering is temporarily unavailable.",
              false
            )
          );
        }
      });
  };
}

export {
  fetchMenu,
  registerCardAndSubmitOrder,
  submitOrder,
  updateOrderingInfo,
  validateAndPutContactInfo,
  validateAndSaveContactField,
  validateContactFieldOnBlur,
};
